import { FC, useState } from 'react'
import { FileText, Copy, Check, Maximize2, ClipboardList, Stethoscope, ListChecks, ArrowRightLeft } from 'lucide-react'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '../../lib/utils'

interface ArtifactCardProps {
  type: 'hpi' | 'assessment' | 'sweep' | 'ipass' | 'custom'
  title: string
  content: string
  metadata?: {
    model?: string
    tokens?: number
    latency?: number
    [key: string]: any
  }
  onExpand?: () => void
}

/**
 * Inline artifact display card - Clinical Intelligence System design
 * Shows clinical artifacts (HPI, A&P, etc.) in chat as premium rich cards
 */
export const ArtifactCard: FC<ArtifactCardProps> = ({
  type,
  title,
  content,
  metadata,
  onExpand
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  const getTypeConfig = () => {
    switch (type) {
      case 'hpi':
        return {
          color: 'bg-clinical-teal/10 border-clinical-teal/30 hover:border-clinical-teal/50',
          badgeColor: 'bg-clinical-teal/20 text-clinical-teal',
          icon: <Stethoscope className="h-4 w-4" />,
          label: 'HPI'
        }
      case 'assessment':
        return {
          color: 'bg-clinical-sage/10 border-clinical-sage/30 hover:border-clinical-sage/50',
          badgeColor: 'bg-clinical-sage/20 text-clinical-sage',
          icon: <ClipboardList className="h-4 w-4" />,
          label: 'A&P'
        }
      case 'sweep':
        return {
          color: 'bg-clinical-cyan/10 border-clinical-cyan/30 hover:border-clinical-cyan/50',
          badgeColor: 'bg-clinical-cyan/20 text-clinical-cyan',
          icon: <ListChecks className="h-4 w-4" />,
          label: 'Sweep'
        }
      case 'ipass':
        return {
          color: 'bg-clinical-amber/10 border-clinical-amber/30 hover:border-clinical-amber/50',
          badgeColor: 'bg-clinical-amber/20 text-clinical-amber',
          icon: <ArrowRightLeft className="h-4 w-4" />,
          label: 'I-PASS'
        }
      default:
        return {
          color: 'bg-surface-elevated border-border/50 hover:border-primary/30',
          badgeColor: 'bg-primary/10 text-primary',
          icon: <FileText className="h-4 w-4" />,
          label: 'Custom'
        }
    }
  }

  const config = getTypeConfig()

  return (
    <Card className={cn(
      "overflow-hidden border-2 rounded-xl transition-all duration-300",
      config.color
    )}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border/30 bg-background/50 backdrop-blur-sm px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center",
            config.badgeColor.replace('text-', 'bg-').replace('/20', '/15')
          )}>
            {config.icon}
          </div>
          <div>
            <h4 className="font-semibold text-sm">{title}</h4>
            <span className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase mt-0.5",
              config.badgeColor
            )}>
              {config.label}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {metadata && (
            <span className="mr-2 text-[10px] text-muted-foreground font-mono">
              {metadata.tokens && `${metadata.tokens} tok`}
              {metadata.latency && ` • ${metadata.latency}ms`}
            </span>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-8 w-8 p-0 rounded-lg hover:bg-background"
            title="Copy to clipboard"
            aria-label={`Copy ${title} to clipboard`}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-clinical-sage" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </Button>

          {onExpand && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // Fix #182: Removed PHI logging — content contains clinical data
                onExpand()
              }}
              className="h-8 w-8 p-0 rounded-lg hover:bg-background"
              title="Expand to editor panel"
              aria-label={`Expand ${title} to full panel`}
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-h-80 overflow-y-auto p-4 scrollbar-hide">
        <div className={cn(
          // Base prose with typography plugin defaults
          "prose prose-sm max-w-none dark:prose-invert",
          // Headings use display font
          "prose-headings:font-display",
          // Code styling
          "prose-code:text-primary prose-code:bg-surface prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none"
        )}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content}
          </ReactMarkdown>
        </div>
      </div>

      {/* Footer with metadata */}
      {metadata?.version && (
        <div className="border-t border-border/30 bg-surface/50 px-4 py-2 text-[10px] text-muted-foreground flex items-center gap-3">
          <span>v{metadata.version}</span>
        </div>
      )}
    </Card>
  )
}
